import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { performanceService } from '../../services/performanceService';
import DatadogChartsGrid from './DatadogCharts';

const ReportsTab = ({ selectedExecutionId }) => {
  const [executions, setExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState('');
  const [executionDetails, setExecutionDetails] = useState(null);
  const [actionStats, setActionStats] = useState([]);
  const [hierarchicalData, setHierarchicalData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [loading, setLoading] = useState(false);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDataTable, setShowDataTable] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showDatadogMetrics, setShowDatadogMetrics] = useState(false);
  const [viewMode, setViewMode] = useState('modern'); // 'modern' or 'legacy'

  useEffect(() => {
    fetchExecutions();
  }, []);

  useEffect(() => {
    if (selectedExecutionId) {
      setSelectedExecution(selectedExecutionId);
    }
  }, [selectedExecutionId]);

  useEffect(() => {
    if (selectedExecution) {
      fetchExecutionDetails();
      fetchActionStats();
    }
  }, [selectedExecution]);

  const fetchExecutions = async () => {
    try {
      setExecutionsLoading(true);
      setError('');
      console.log('Fetching executions...');
      
      const response = await performanceService.getExecutions();
      console.log('Executions response:', response);
      
      if (response && response.data) {
        // Show all executions, not just completed ones, but prioritize completed ones
        const allExecutions = response.data;
        const sortedExecutions = allExecutions.sort((a, b) => {
          // First sort by status (completed first), then by date
          if (a.status === 'completed' && b.status !== 'completed') return -1;
          if (a.status !== 'completed' && b.status === 'completed') return 1;
          return new Date(b.executionDate) - new Date(a.executionDate);
        });
        
        setExecutions(sortedExecutions);
        console.log(`Found ${sortedExecutions.length} executions`);
        
        if (sortedExecutions.length === 0) {
          setError('No executions found. Please create and run a performance test first.');
        }
      } else {
        setError('No execution data received from server');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch executions';
      setError(`Failed to fetch executions: ${errorMessage}`);
      console.error('Error fetching executions:', err);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const fetchExecutionDetails = async () => {
    try {
      setLoading(true);
      const response = await performanceService.getExecutionSummary(selectedExecution);
      setExecutionDetails(response.data);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch execution details';
      setError(`Failed to fetch execution details: ${errorMessage}`);
      console.error('Error fetching execution details:', err);
      if (err.response?.status === 404) {
        setSelectedExecution('');
        setExecutionDetails(null);
        setActionStats([]);
        setHierarchicalData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActionStats = async () => {
    try {
      const response = await performanceService.getExecutionLabelStats(selectedExecution);
      const stats = response.data || [];
      setActionStats(stats);
      buildHierarchicalStructure(stats);
    } catch (err) {
      console.error('Error fetching action stats:', err);
      setActionStats([]);
      setHierarchicalData([]);
    }
  };

  // Build hierarchical structure from flat action stats using pipe delimiter
  const buildHierarchicalStructure = (stats) => {
    try {
      // Map to store all nodes by their full label for quick lookup
      const nodeMap = new Map();
      const rootNodes = [];

      // First pass: create all nodes and organize by their segments
      stats.forEach(item => {
        // Get label and ensure it's a string
        const rawLabel = item.Label || item.label || '';
        if (typeof rawLabel !== 'string') return;

        const label = rawLabel.trim();

        // Skip empty labels or whitespace-only labels
        if (!label) return;

        // Split by pipe delimiter to get segments
        const segments = label.split('|').map(seg => seg.trim()).filter(seg => seg.length > 0);

        // Skip if no valid segments after filtering
        if (segments.length === 0) return;

        // Create node object
        const node = {
          fullLabel: label,
          segments: segments,
          level: segments.length - 1,
          data: item,
          children: [],
          Label: label // Keep the Label property for consistency
        };

        // Add all properties from the original item
        Object.keys(item).forEach(key => {
          if (!node.hasOwnProperty(key)) {
            node[key] = item[key];
          }
        });

        nodeMap.set(label, node);
      });

      // Second pass: build parent-child relationships
      nodeMap.forEach((node, fullLabel) => {
        if (node.segments.length === 1) {
          // This is a root node
          rootNodes.push(node);
        } else {
          // Find parent by removing the last segment
          const parentSegments = node.segments.slice(0, -1);
          const parentLabel = parentSegments.join(' | ');

          const parentNode = nodeMap.get(parentLabel);
          if (parentNode) {
            // Add this node as a child of its parent
            parentNode.children.push(node);
          } else {
            // Parent doesn't exist as a data point, treat as root
            rootNodes.push(node);
          }
        }
      });

      // Sort root nodes and children for consistent display
      const sortNodes = (nodes) => {
        nodes.sort((a, b) => a.fullLabel.localeCompare(b.fullLabel));
        nodes.forEach(node => {
          if (node.children.length > 0) {
            sortNodes(node.children);
          }
        });
      };

      sortNodes(rootNodes);
      setHierarchicalData(rootNodes);
    } catch (err) {
      console.error('Error building hierarchical structure:', err);
      setHierarchicalData([]);
    }
  };

  // Toggle node expansion (works for any level)
  const toggleNode = (nodeLabel) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeLabel]: !prev[nodeLabel]
    }));
  };

  const handleExecutionChange = (event) => {
    setSelectedExecution(event.target.value);
  };

  const handleRefresh = () => {
    fetchExecutions();
  };

  // Enhanced date formatting function
  const formatExecutionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      case 'ignored': return 'default';
      default: return 'default';
    }
  };

  // Recursive function to render hierarchical rows
  const renderHierarchicalRows = (nodes, depth = 0) => {
    const rows = [];

    nodes.forEach((node, index) => {
      const nodeLabel = node.fullLabel || node.Label || '';
      const isExpanded = expandedNodes[nodeLabel] || false;
      const hasChildren = node.children && node.children.length > 0;

      // Determine background color based on depth
      const getBgColor = (depth) => {
        const colors = ['primary.50', 'grey.50', 'grey.100'];
        return colors[Math.min(depth, colors.length - 1)];
      };

      const getHoverColor = (depth) => {
        const colors = ['primary.100', 'grey.100', 'grey.200'];
        return colors[Math.min(depth, colors.length - 1)];
      };

      // Parent/Node Row
      rows.push(
        <TableRow
          key={`node-${depth}-${index}-${nodeLabel}`}
          sx={{
            bgcolor: getBgColor(depth),
            '&:hover': { bgcolor: getHoverColor(depth) },
            cursor: hasChildren ? 'pointer' : 'default',
            transition: 'background-color 0.2s'
          }}
          onClick={hasChildren ? () => toggleNode(nodeLabel) : undefined}
        >
          <TableCell>
            {hasChildren && (
              <IconButton size="small">
                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            )}
          </TableCell>
          <TableCell sx={{ pl: 2 + depth * 4 }}>
            <Typography
              sx={{
                fontWeight: depth === 0 ? 700 : 600 - (depth * 100),
                color: depth === 0 ? 'primary.main' : 'text.primary'
              }}
            >
              {nodeLabel}
            </Typography>
          </TableCell>
          <TableCell align="right">{node['# Samples'] || '-'}</TableCell>
          <TableCell align="right">{node.Average || '-'}</TableCell>
          <TableCell align="right">{node.Median || '-'}</TableCell>
          <TableCell align="right">{node['90% Line'] || '-'}</TableCell>
          <TableCell align="right">{node['95% Line'] || '-'}</TableCell>
          <TableCell align="right">{node['99% Line'] || '-'}</TableCell>
          <TableCell align="right">{node.Min || '-'}</TableCell>
          <TableCell align="right">{node.Max || '-'}</TableCell>
          <TableCell align="right">
            <Chip
              label={node['Error %'] || '0.00%'}
              size="small"
              color={(parseFloat(node['Error %']) || 0) === 0 ? 'success' : 'error'}
              sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
            />
          </TableCell>
          <TableCell align="right">{node.Throughput || '-'}</TableCell>
        </TableRow>
      );

      // Recursively render children if expanded
      if (isExpanded && hasChildren) {
        rows.push(...renderHierarchicalRows(node.children, depth + 1));
      }
    });

    return rows;
  };

  return (
    <Box>
      <Box mb={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            Performance Test Reports
          </Typography>
          <Tooltip title="Refresh executions">
            <IconButton onClick={handleRefresh} disabled={executionsLoading}>
              {executionsLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <FormControl sx={{ minWidth: 400 }} disabled={executionsLoading}>
          <InputLabel>Select Execution</InputLabel>
          <Select
            value={selectedExecution}
            onChange={handleExecutionChange}
            label="Select Execution"
          >
            {executions.map((execution) => (
              <MenuItem key={execution._id} value={execution._id}>
                <Typography>
                  {execution.projectId?.projectName || 'Unknown Project'} - {execution.release} - {execution.build} | {formatExecutionDate(execution.executionDate)} | {execution.userLoad} users
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {executionsLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading executions...
          </Typography>
        </Box>
      )}

      {!executionsLoading && executions.length === 0 && !error && (
        <Alert severity="info">
          No performance test executions found. Please create and run a performance test first.
        </Alert>
      )}

      {!executionsLoading && executions.length > 0 && !selectedExecution && (
        <Alert severity="info">
          Please select an execution to view the performance report.
        </Alert>
      )}

      {loading && selectedExecution && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading execution details...
          </Typography>
        </Box>
      )}

      {selectedExecution && executionDetails && !loading && (
        <Box>
          {/* Execution Details - Moved to Top */}
          <Paper sx={{ mb: 4, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Execution Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Project:</strong> {executionDetails.execution.projectId?.projectName}</Typography>
                <Typography variant="body2"><strong>Release:</strong> {executionDetails.execution.release}</Typography>
                <Typography variant="body2"><strong>Build:</strong> {executionDetails.execution.build}</Typography>
                <Typography variant="body2"><strong>User Load:</strong> {executionDetails.execution.userLoad}</Typography>
                <Typography variant="body2"><strong>Status:</strong> 
                  <Chip 
                    label={executionDetails.execution.status} 
                    size="small" 
                    color={getStatusColor(executionDetails.execution.status)}
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Instance:</strong> {executionDetails.execution.instanceName}</Typography>
                <Typography variant="body2"><strong>CPUs:</strong> {executionDetails.execution.cpus}</Typography>
                <Typography variant="body2"><strong>Memory:</strong> {executionDetails.execution.memory}</Typography>
                <Typography variant="body2"><strong>CSV File:</strong> {executionDetails.execution.csvFileName}</Typography>
                <Typography variant="body2"><strong>Execution Date:</strong> {formatExecutionDate(executionDetails.execution.executionDate)}</Typography>
              </Grid>
            </Grid>
            
            {/* Datadog Integration Section */}
            {executionDetails.execution.datadogLink && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Infrastructure Monitoring
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Link 
                    href={executionDetails.execution.datadogLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button 
                      variant="outlined" 
                      startIcon={<OpenInNewIcon />}
                      size="small"
                    >
                      View Datadog Dashboard
                    </Button>
                  </Link>
                  <Button 
                    variant="contained" 
                    onClick={() => setShowDatadogMetrics(true)}
                    size="small"
                    sx={{ 
                      bgcolor: '#632CA6',
                      '&:hover': { bgcolor: '#4A1F7A' }
                    }}
                  >
                    Datadog Metrics
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Summary Cards - Professional Thomson Reuters Theme Colors */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #1A2E22 0%, #2C4A3A 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    Actions
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.actions || executionDetails.summary.uniqueLabelsCount || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #D74B00 0%, #E67234 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    Avg Response Time
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.averageResponseTime || executionDetails.summary.avgResponseTime || 0}ms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    90th Percentile
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.percentile90 || executionDetails.summary.avg90thPercentile || 0}ms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    95th Percentile
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.percentile95 || executionDetails.summary.avg95thPercentile || 0}ms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    Throughput
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.throughput || executionDetails.summary.avgThroughput || 0} req/s
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ 
                background: (executionDetails.summary.errorRate || executionDetails.summary.avgErrorRate || 0) > 5 
                  ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                  : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                '& .MuiCardContent-root': { color: 'white' }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom sx={{ opacity: 0.9 }}>
                    Error Rate
                  </Typography>
                  <Typography variant="h4" color="inherit">
                    {executionDetails.summary.errorRate || executionDetails.summary.avgErrorRate || 0}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Results Table with View Toggle */}
          {(hierarchicalData.length > 0 || actionStats.length > 0) && (
            <Paper sx={{ mt: 4 }}>
              <Box p={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Action Results ({viewMode === 'modern' ? `${hierarchicalData.length} root nodes` : `${actionStats.length} total actions`})
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      variant={viewMode === 'modern' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('modern')}
                      size="small"
                    >
                      Modern View
                    </Button>
                    <Button
                      variant={viewMode === 'legacy' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('legacy')}
                      size="small"
                    >
                      Legacy View
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowDataTable(!showDataTable)}
                      startIcon={showDataTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {showDataTable ? 'Hide Table' : 'Show Table'}
                    </Button>
                  </Box>
                </Box>

                <Collapse in={showDataTable}>
                  {viewMode === 'modern' ? (
                    /* Modern View - Hierarchical with parent-child */
                    <>
                      <TableContainer
                        sx={{
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          borderRadius: 2,
                          overflowX: 'auto',
                          maxWidth: '100%',
                          '&::-webkit-scrollbar': {
                            height: '12px',
                            width: '12px'
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: 2
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: 2,
                            '&:hover': {
                              backgroundColor: '#555'
                            }
                          }
                        }}
                      >
                        <Table size="small" sx={{ minWidth: 1200 }}>
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: 'grey.50',
                                '& .MuiTableCell-head': {
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                  color: 'text.primary',
                                  borderBottom: '2px solid',
                                  borderColor: 'divider',
                                  py: 2
                                }
                              }}
                            >
                              <TableCell width="40px"></TableCell>
                              <TableCell>Label</TableCell>
                              <TableCell align="right"># Samples</TableCell>
                              <TableCell align="right">Average (ms)</TableCell>
                              <TableCell align="right">Median (ms)</TableCell>
                              <TableCell align="right">90% Line (ms)</TableCell>
                              <TableCell align="right">95% Line (ms)</TableCell>
                              <TableCell align="right">99% Line (ms)</TableCell>
                              <TableCell align="right">Min (ms)</TableCell>
                              <TableCell align="right">Max (ms)</TableCell>
                              <TableCell align="right">Error %</TableCell>
                              <TableCell align="right">Throughput</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {renderHierarchicalRows(
                              hierarchicalData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={hierarchicalData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                      />
                    </>
                  ) : (
                    /* Legacy View - Flat table with all stats */
                    <>
                      <TableContainer
                        sx={{
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          borderRadius: 2,
                          overflowX: 'auto',
                          maxWidth: '100%',
                          '&::-webkit-scrollbar': {
                            height: '12px',
                            width: '12px'
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: 2
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: 2,
                            '&:hover': {
                              backgroundColor: '#555'
                            }
                          }
                        }}
                      >
                        <Table size="small" sx={{ minWidth: 1600 }}>
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: 'grey.50',
                                '& .MuiTableCell-head': {
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                  color: 'text.primary',
                                  borderBottom: '2px solid',
                                  borderColor: 'divider',
                                  py: 2
                                }
                              }}
                            >
                              <TableCell>Label</TableCell>
                              <TableCell align="right"># Samples</TableCell>
                              <TableCell align="right">Average (ms)</TableCell>
                              <TableCell align="right">Median (ms)</TableCell>
                              <TableCell align="right">90% Line (ms)</TableCell>
                              <TableCell align="right">95% Line (ms)</TableCell>
                              <TableCell align="right">99% Line (ms)</TableCell>
                              <TableCell align="right">Min (ms)</TableCell>
                              <TableCell align="right">Max (ms)</TableCell>
                              <TableCell align="right">Error %</TableCell>
                              <TableCell align="right">Throughput</TableCell>
                              <TableCell align="right">Received KB/sec</TableCell>
                              <TableCell align="right">Sent KB/sec</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {actionStats
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((stat, index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    '&:hover': { bgcolor: 'grey.50' },
                                    transition: 'background-color 0.2s'
                                  }}
                                >
                                  <TableCell>
                                    <Typography variant="body2">
                                      {stat.Label || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">{stat['# Samples'] || '-'}</TableCell>
                                  <TableCell align="right">{stat.Average || '-'}</TableCell>
                                  <TableCell align="right">{stat.Median || '-'}</TableCell>
                                  <TableCell align="right">{stat['90% Line'] || '-'}</TableCell>
                                  <TableCell align="right">{stat['95% Line'] || '-'}</TableCell>
                                  <TableCell align="right">{stat['99% Line'] || '-'}</TableCell>
                                  <TableCell align="right">{stat.Min || '-'}</TableCell>
                                  <TableCell align="right">{stat.Max || '-'}</TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={stat['Error %'] || '0.00%'}
                                      size="small"
                                      color={(parseFloat(stat['Error %']) || 0) === 0 ? 'success' : 'error'}
                                      sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">{stat.Throughput || '-'}</TableCell>
                                  <TableCell align="right">{stat['Received KB/sec'] || '-'}</TableCell>
                                  <TableCell align="right">{stat['Sent KB/sec'] || '-'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={actionStats.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                      />
                    </>
                  )}
                </Collapse>
              </Box>
            </Paper>
          )}

          {/* Show message if no hierarchical data */}
          {actionStats.length > 0 && hierarchicalData.length === 0 && (
            <Alert severity="info" sx={{ mt: 4 }}>
              No hierarchical transaction structure found. Use pipe delimiter (|) to create parent-child relationships (e.g., "parent | child | action").
            </Alert>
          )}
        </Box>
      )}

      {/* Datadog Metrics Dialog */}
      <Dialog 
        open={showDatadogMetrics} 
        onClose={() => setShowDatadogMetrics(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Infrastructure Metrics</Typography>
            <IconButton 
              onClick={() => setShowDatadogMetrics(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <DatadogChartsGrid />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDatadogMetrics(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsTab;
